import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Heart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import SEO from "../components/SEO";

const DonateSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    // Check if payment was successful based on Stripe session
    const sessionId = searchParams.get("session_id");
    
    if (sessionId) {
      // Verify payment with backend
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/donations/verify/${sessionId}`)
        .then(res => res.json())
        .then(data => {
          if (data.payment_status === "paid") {
            setStatus("success");
          } else {
            setStatus("pending");
          }
        })
        .catch(err => {
          console.error("Verification error:", err);
          setStatus("pending");
        });
    } else {
      setStatus("pending");
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Donation Success"
        description="Thank you for your donation to The Grazing Trust"
        noindex={true}
      />
      
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <Card className="text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              {status === "success" ? (
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 size={48} className="text-green-600" />
                </div>
              ) : (
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Heart size={48} className="text-emerald-600" />
                </div>
              )}
            </div>
            <CardTitle className="text-3xl font-bold">
              {status === "success" ? "Thank You for Your Donation!" : "Donation Processing"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {status === "success" ? (
              <>
                <p className="text-lg text-gray-700">
                  Your generous donation has been successfully processed!
                </p>
                <p className="text-gray-600">
                  You will receive a confirmation email from Stripe shortly with your receipt. 
                  Your payment has been securely processed.
                </p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 my-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">
                    Your contribution makes a real difference
                  </h3>
                  <p className="text-sm text-emerald-800">
                    Every donation helps us provide care, rehabilitation, and secure futures for horses in need across Scotland.
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-lg text-gray-700">
                  Your donation is being processed.
                </p>
                <p className="text-gray-600">
                  If you completed the payment setup, you should receive a confirmation email shortly.
                </p>
              </>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link 
                to="/" 
                className="inline-flex items-center justify-center px-6 py-3 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors"
              >
                Return to Homepage
              </Link>
              <Link 
                to="/about" 
                className="inline-flex items-center justify-center px-6 py-3 border border-emerald-700 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors"
              >
                Learn More About Us
              </Link>
            </div>

            <div className="pt-6 border-t mt-8">
              <p className="text-sm text-gray-600">
                Questions about your donation? Contact us at{" "}
                <a 
                  href="mailto:info@thegrazingtrust.com" 
                  className="text-emerald-700 hover:underline"
                >
                  info@thegrazingtrust.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-12 text-center">
          <h2 className="text-2xl font-bold mb-6">What Happens Next?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg border">
              <div className="text-3xl font-bold text-emerald-700 mb-2">1</div>
              <h3 className="font-semibold mb-2">Payment Complete</h3>
              <p className="text-sm text-gray-600">
                Your card payment has been processed securely
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <div className="text-3xl font-bold text-emerald-700 mb-2">2</div>
              <h3 className="font-semibold mb-2">Receipt Sent</h3>
              <p className="text-sm text-gray-600">
                Check your email for the payment receipt
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <div className="text-3xl font-bold text-emerald-700 mb-2">3</div>
              <h3 className="font-semibold mb-2">Impact</h3>
              <p className="text-sm text-gray-600">
                Your support helps horses in need receive care
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonateSuccessPage;
